import { NgModule } from '@angular/core';
import { RouterModule, Routes, UrlMatchResult, UrlSegment } from '@angular/router';
import { FUNCIONALIDADE } from '@gestor-web/@core/auth/funcionalidades';
import { AuthGuard, FeatureAccessGuard } from '@gestor-web/@core/auth/guard';
import { OperadoraWorkspaceGuard } from '@gestor-web/@core/auth/guard/operadora-workspace.guard';
import { ProfileGuard } from '@gestor-web/@core/auth/guard/profile.guard';
import { ReadyGuard } from '@gestor-web/@core/guard/ready.guard';
import { LandingPageComponent } from '@gestor-web/app/shared/components';
import { HomeResolverComponent } from '@gestor-web/app/home-resolver.omponent';
import { AppMainComponent } from './layout/app.main.component';
import { AppAccessdeniedComponent } from './pages/public/accessdenied/app.accessdenied.component';
import { AppErrorComponent } from './pages/public/error/app.error.component';
import { LogoutComponent } from './pages/public/logout/logout.component';
import { AppNotfoundComponent } from './pages/public/notfound/app.notfound.component';
import { AppOfflineComponent } from './pages/public/offline/app.offline.component';

function simuladorMatcher(segments: UrlSegment[]): UrlMatchResult {
  // console.log('simuladorMatcher ' + segments.map(s => s.path));

  if (!segments.length || !['simulador-idss', 'simulador-mra'].includes(segments[0].path)) {
    return null;
  }

  return {
    consumed: [segments[0]],
    posParams: {
      tipo: segments[0],
    },
  };
}

const APP_ROUTES: Routes = [
  {
    path: '',
    component: AppMainComponent,
    canActivate: [AuthGuard, ProfileGuard],
    runGuardsAndResolvers: 'always',
    children: [
      {
        path: '',
        pathMatch: 'full',
        component: HomeResolverComponent,
        // redirectTo: 'dados-setor/idss/resultados-por-operadora',
      },
      {
        path: 'operadora/:id',
        canActivate: [OperadoraWorkspaceGuard, ReadyGuard],
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        children: [
          {
            path: '',
            data: { title: 'Dashboard' },
            loadChildren: () => import('./pages/private/dashboard/dashboard.module').then(m => m.DashboardModule),
          },
          {
            path: 'guias-tiss',
            data: { title: 'Guia TISS' },
            loadChildren: () => import('./pages/private/guias-tiss/guias-tiss.module').then(m => m.GuiasTissModule),
          },
          {
            // Aliases da nova estrutura de menu "Críticas": o link legado (guias-tiss/critica-guias e
            // guias-tiss/retornos-ans) continua intacto acima — já enviado a clientes pelo time de operações.
            // Precisam vir antes do `path: 'criticas'` abaixo para não serem capturados pelo CriticasModule.
            path: 'criticas/painel-criticas',
            redirectTo: 'guias-tiss/critica-guias',
          },
          {
            path: 'criticas/retornos-ans',
            redirectTo: 'guias-tiss/retornos-ans',
          },
          {
            path: 'criticas',
            data: { title: 'Críticas' },
            loadChildren: () => import('./pages/private/criticas/criticas.module').then(m => m.CriticasModule),
          },
          {
            path: 'outras-remuneracoes',
            data: { title: 'Outras remunerações' },
            loadChildren: () =>
              import('./pages/private/outras-remuneracoes/outras-remuneracoes.module').then(
                m => m.OutrasRemuneracoesModule,
              ),
          },
          {
            path: 'nips',
            data: { title: 'NIPs' },
            loadChildren: () => import('./pages/private/nips/nips.module').then(m => m.NipsModule),
          },
          {
            path: 'poc',
            data: { title: 'Poc' },
            loadChildren: () => import('./pages/private/poc/poc.module').then(m => m.PocModule),
          },
          {
            path: 'arquivos',
            data: { title: 'Arquivos' },
            loadChildren: () => import('./pages/private/arquivos/arquivos.module').then(m => m.ArquivosModule),
          },
          {
            path: 'analises',
            data: { title: 'Análises' },
            loadChildren: () => import('./pages/private/analises/analises.module').then(m => m.AnalisesModule),
          },
          {
            // path: 'simulador-idss',
            matcher: simuladorMatcher,
            data: { title: 'Simulador IDSS' },
            loadChildren: () =>
              import('./pages/private/simulador-idss/simulador-idss.module').then(m => m.SimuladorIdssModule),
          },
          {
            path: 'redimensionamento-rede',
            data: { title: 'Redimensionamento de rede pela RN585' },
            loadChildren: () =>
              import('./pages/private/redimensionamento-rede/redimensionamento-rede.module').then(
                m => m.RedimensionamentoRedeModule,
              ),
          },
          {
            path: 'redimensionamento-nao-hospitalar',
            data: { title: 'Redimensionamento de rede não hospitalar' },
            loadChildren: () =>
              import(
                './pages/private/redimensionamento-nao-hospitalar/redimensionamento-nao-hospitalar.module'
              ).then(m => m.RedimensionamentoNaoHospitalarModule),
          },
          {
            path: 'minha-operadora',
            data: { title: 'Minha operadora' },
            loadChildren: () =>
              import('./pages/private/minha-operadora/minha-operadora.module').then(m => m.MinhaOperadoraModule),
          },
        ],
      },
      {
        path: 'dados-setor',
        data: { title: 'Dados do setor' },
        loadChildren: () => import('./pages/private/dados-setor/dados-setor.module').then(m => m.DadosSetorModule),
      },
      {
        canActivate: [FeatureAccessGuard],
        path: 'operadoras',
        data: { title: 'Operadoras do sistema', featureCode: FUNCIONALIDADE.OPERADORAS },
        loadChildren: () => import('./pages/private/operadoras/operadoras.module').then(m => m.OperadorasModule),
      },
      {
        canActivate: [FeatureAccessGuard],
        path: 'usuarios',
        data: { title: 'Usuários', featureCode: FUNCIONALIDADE.USUARIOS },
        loadChildren: () => import('./pages/private/usuarios/usuarios.module').then(m => m.UsuariosModule),
      },
      {
        path: 'profile',
        data: { title: 'Meu perfil' },
        loadChildren: () => import('./pages/private/user-profile/user-profile.module').then(m => m.UserProfileModule),
      },
    ],
  },
  {
    path: 'cliente',
    component: LandingPageComponent,
    loadChildren: () => import('./pages/private/cliente/cliente.module').then(m => m.ClienteModule),
  },
  { path: 'offline', component: AppOfflineComponent },
  { path: 'error', component: AppErrorComponent },
  { path: 'access', component: AppAccessdeniedComponent },
  { path: 'notfound', component: AppNotfoundComponent },
  { path: 'logout', component: LogoutComponent },
  { path: '**', redirectTo: '/notfound' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(APP_ROUTES, {
      scrollPositionRestoration: 'enabled',
      // enableTracing: true, // debug only
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
